import { useMutation, useQueryClient } from "react-query"
import { useApiClient } from "../../api-client/api-client.hooks"
import { TQueryOptions } from "../../timenotes-query/timenotes-query.types"
import { TimeLogFormItemProps } from "../../time-logs/time-logs.types"
import { FormikErrors } from "formik"
import { removeUndefinedValues } from "../../utils/remove-values"
import moment, { Moment } from "moment-timezone"

type Params = {
  params?: any
  options?: TQueryOptions
}

export const useUpdateActiveTrackerMutation = ({ params, options }: Params = {}) => {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  const query = useMutation(
    ['active-tracker', 'update'],
    async (args: TimeLogFormItemProps & { startedAt?: Moment}) => {
      const requestParams = {
        taskId: args.task?.id,
        description: args.description,
        tags: Array.isArray(args.tags) ? (args.tags || []).map((tag) => tag.id) : undefined,
        isBillable: args.isBillable,
        startedAt: args.startedAt ? args.startedAt.format() : undefined
      }

      const response = await apiClient.patch('/active_tracker', {
        activeTracker: removeUndefinedValues(requestParams)
      })

      if (!response.ok) {
        throw response.errors as FormikErrors<TimeLogFormItemProps>
      }

      return {
        activeTracker: response.activeTracker
      }
    },
    {
      onMutate: async (newData) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries('active-tracker')

        // Snapshot the previous value
        const previousData = queryClient.getQueryData('active-tracker')

        // Optimistically update to the new value
        queryClient.setQueryData('active-tracker', (old: any) => ({
          ...old,
          activeTracker: {
            ...old.activeTracker,
            ...(newData.task !== undefined && { task: newData.task }),
            //...(newData.task?.project !== undefined && { project: newData.task.project }),
            ...(newData.description !== undefined && { description: newData.description }),
            ...(newData.tags !== undefined && { tags: newData.tags || [] }),
            ...(newData.isBillable !== undefined && { isBillable: newData.isBillable }),
            ...(newData.startedAt !== undefined && { startedAt: newData.startedAt.format() })
          }
        }))

        // Return a context object with the snapshotted value
        return { previousData }
      },
      onError: (err, newData, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousData) {
          queryClient.setQueryData('active-tracker', context.previousData)
        }
      },
      onSettled: () => {
        // Always refetch after error or success to ensure we're in sync with the server
        queryClient.invalidateQueries('active-tracker')
      }
    }
  )

  return query
}

export default useUpdateActiveTrackerMutation 